import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  onSnapshot,
  getDocs,
  where,
  limit,
  startAfter,
  orderBy,
  doc,
  getDoc,
  getCountFromServer,
  startAt,
  endAt,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheckCircle,
  faSearch,
  faSortUp,
  faSortDown,
  faChevronDown,
  faUsers,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { InputField } from "@cakrawalajakarta/shared-components";

const PAGE_SIZE = 50;
const DEFAULT_EVENT_ID = "JOTR2026";
const DEFAULT_COLLECTION_NAME = "pendaftaranJOTR2026";
const DEFAULT_EVENT_TITLE = "JAKARTA OPEN TRAIL RUN";
const ALL_CATEGORY = { distance: "all", displayName: "ALL", description: "ALL" };
const EMPTY_FALLBACK_CATEGORIES = [];

const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4 hidden md:table-cell">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
  </tr>
));

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`h-10 px-4 flex items-center justify-center rounded-xl text-xs sm:text-sm font-medium transition-colors ${
      selectedCategory === category.distance
        ? "bg-lime-600 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter peserta berdasarkan kategori ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

export const EntryListPage = React.memo(({
  eventId = DEFAULT_EVENT_ID,
  defaultCollectionName = DEFAULT_COLLECTION_NAME,
  defaultEventTitle = DEFAULT_EVENT_TITLE,
  fallbackCategories = EMPTY_FALLBACK_CATEGORIES,
  useFirestoreTitle = false,
} = {}) => {
  const [state, setState] = useState({
    participants: [],
    // Removed allParticipants, filteredParticipants, displayedParticipants 
    // as we are now doing server-side only. We can keep participants.
    searchTerm: "",
    eventTitle: defaultEventTitle,
    selectedCategory: "all",
    categories: [ALL_CATEGORY],
    loading: true,
    loadingMore: false,
    error: null,
    errorUrl: null, 
    collectionName: defaultCollectionName,
    lastVisible: null,
    totalParticipants: 0, 
    sortGenderDirection: null,
    hasMore: true,
    searchPageIndex: 0,
    categoriesLoaded: false,
    selectedGroup: null,
  });

  // ... (refs, etc. same as before)
  const isMounted = useRef(true);
  const unsubscribeRef = useRef({
    participants: null,
    total: null,
  });
  const searchPageIndexRef = useRef(0);

  const debouncedSearchTerm = useDebounce(state.searchTerm, 500); // 500ms delay for server search

  // ... (useEffects)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      Object.values(unsubscribeRef.current).forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const eventRef = doc(db, "semuaKegiatan", eventId);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        const rawCategories = eventData.categories || fallbackCategories;

        const mappedCategories = rawCategories.map((cat) => ({
          distance: cat.distance.toString().trim(),
          displayName: `${cat.distance} KM`,
          description: cat.categoryName || `${cat.distance} KM`,
          categoryName: cat.categoryName || `${cat.distance} KM`,
        }));

        // Sort categories: all first, then by distance ascending
        const sortedCategories = [
          ALL_CATEGORY,
          ...mappedCategories.sort((a, b) => parseInt(a.distance) - parseInt(b.distance)),
        ];

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            eventTitle: useFirestoreTitle ? eventData.title || defaultEventTitle : defaultEventTitle,
            collectionName: eventData.collectionName || defaultCollectionName,
            categories: sortedCategories,
            categoriesLoaded: true,
            loading: true,
          }));
        }
      } else {
        if (fallbackCategories.length === 0) {
          throw new Error(`${eventId} document not found in semuaKegiatan`);
        }
        const mappedFallbackCategories = fallbackCategories.map((cat) => ({
          distance: cat.distance.toString().trim(),
          displayName: cat.displayName || `${cat.distance} KM`,
          description: cat.description || cat.categoryName || `${cat.distance} KM`,
          categoryName: cat.categoryName || `${cat.distance} KM`,
        }));
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            eventTitle: defaultEventTitle,
            collectionName: defaultCollectionName,
            categories: [
              ALL_CATEGORY,
              ...mappedFallbackCategories.sort((a, b) => parseInt(a.distance) - parseInt(b.distance)),
            ],
            categoriesLoaded: true,
            loading: true,
          }));
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          error: `Failed to load ${defaultEventTitle} categories: ${err.message}`,
          loading: false,
          loadingMore: false,
        }));
      }
    }
  }, [eventId, defaultCollectionName, defaultEventTitle, fallbackCategories, useFirestoreTitle]);

  const mapCategoryToFirestore = useCallback(
    (distance) => {
      if (distance === "all") {
        return null;
      }
      const category = state.categories.find(
        (cat) => cat.distance === distance
      );
      let kategori;
      if (distance === "40") {
        // Handle both "Short Distance (40K)" and " Short Distance (40K)" for 40 KM
        kategori = ["Short Distance (40K)", " Short Distance (40K)"];
      } else {
        kategori = category
          ? Array.isArray(category.categoryName)
            ? category.categoryName
            : category.categoryName.trim()
          : null;
      }

      return kategori;
    },
    [state.categories]
  );

  const loadPage = useCallback(
    (collectionName, lastDoc, sortDirection, selectedDistance) => {
      if (!collectionName || !state.categoriesLoaded) {
        setState((prev) => ({ ...prev, loading: false, loadingMore: false }));
        return;
      }

      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        participants: lastDoc ? prev.participants : [],
        error: null,
        errorUrl: null,
      }));

      const processDoc = (doc) => {
        const data = doc.data();
        const mainAccountMember =
          data.kategori === "Mix Group (20K)" && data.namaLengkap
            ? {
                namaDepan: (data.namaLengkap.split(" ")[0] || ""),
                namaBelakang: (data.namaLengkap.split(" ").slice(1).join(" ") || ""),
                gender: data.gender || "Unknown",
                bibName: data.bibName || "",
              }
            : null;
        const participants = Array.isArray(data.participants)
          ? [mainAccountMember, ...data.participants].filter(Boolean)
          : mainAccountMember
          ? [mainAccountMember]
          : [];

        return {
          id: doc.id,
          nama:
            data.teamName && data.kategori === "Mix Group (20K)"
              ? data.teamName
              : data.namaLengkap || "Unknown",
          gender: data.gender || "Unknown",
          kota: data.kota || data.alamat || "Indonesia",
          nationality: data.negara || "Indonesia",
          distance: data.distance || null,
          statusPembayaran: data.transaction_status || "pending",
          administration_status: data.administration_status || "pending",
          category: data.kategori || "Unknown",
          bibTransferredOut: data.bibTransfer?.transferredOut === true,
          participants,
          docRef: doc,
        };
      };

      const participantsCollection = collection(db, collectionName);
      const baseConstraints = [];
      const firestoreCategory = mapCategoryToFirestore(selectedDistance);

      // Category Filter
      if (firestoreCategory && selectedDistance !== "all") {
        const numericDistance = parseInt(selectedDistance, 10);
        if (!isNaN(numericDistance)) {
          baseConstraints.push(where("distance", "==", numericDistance));
        } else if (Array.isArray(firestoreCategory)) {
          baseConstraints.push(where("kategori", "in", firestoreCategory));
        } else {
          baseConstraints.push(where("kategori", "==", firestoreCategory));
        }
      }

      // 2. Search Logic — multi-field (name, order-id, NIK, phone, bib) via
      // searchKeywords, now PAGINATED so all matches are reachable (previously
      // hard-capped at one page). Each page is sorted by name client-side.
      if (debouncedSearchTerm) {
        const termLower = debouncedSearchTerm.toLowerCase();

        // Unsubscribe from any active real-time listener before running search
        if (unsubscribeRef.current.participants) {
          unsubscribeRef.current.participants();
          unsubscribeRef.current.participants = null;
        }

        const searchQuery = lastDoc
          ? query(
              participantsCollection,
              ...baseConstraints,
              where("searchKeywords", "array-contains", termLower),
              startAfter(lastDoc),
              limit(PAGE_SIZE)
            )
          : query(
              participantsCollection,
              ...baseConstraints,
              where("searchKeywords", "array-contains", termLower),
              limit(PAGE_SIZE)
            );

        getDocs(searchQuery)
          .then((snap) => {
            if (!isMounted.current) return;
            const page = snap.docs.map(processDoc).filter(p => !p.bibTransferredOut).sort((a, b) => {
              const nameA = (a.namaLengkap || "").toLowerCase();
              const nameB = (b.namaLengkap || "").toLowerCase();
              if (sortDirection === "desc") return nameB.localeCompare(nameA);
              return nameA.localeCompare(nameB);
            });

            setState((prev) => ({
              ...prev,
              participants: lastDoc ? [...prev.participants, ...page] : page,
              lastVisible: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : prev.lastVisible,
              loading: false,
              loadingMore: false,
              hasMore: snap.size === PAGE_SIZE,
            }));
          })

          .catch((err) => {
            if (!isMounted.current) return;
            console.error("[EntryList] Search Query Error:", err);
            const urlMatch = err.message.match(/(https:\/\/console\.firebase\.google\.com[^\s]+)/);
            setState((prev) => ({
              ...prev,
              error: `Failed to load: ${err.message}`,
              errorUrl: urlMatch ? urlMatch[1] : null,
              loading: false,
              loadingMore: false,
            }));
          });
        return; // Skip onSnapshot setup for search queries
      } else {
        // If sorting by Gender is active
        if (sortDirection) {
           baseConstraints.push(orderBy("gender", sortDirection));
        }
        // Always order by createdAt desc if possible, or leave default.
        baseConstraints.push(orderBy("createdAt", "desc"));
      }

      const qParticipants = lastDoc
        ? query(
            participantsCollection,
            ...baseConstraints,
            startAfter(lastDoc),
            limit(PAGE_SIZE)
          )
        : query(
            participantsCollection,
            ...baseConstraints,
            limit(PAGE_SIZE)
          );

      const unsubscribe = onSnapshot(
        qParticipants,
        (snapshot) => {
          if (!isMounted.current) return;

          if (snapshot.empty) {
            setState((prev) => ({
              ...prev,
              participants: lastDoc ? prev.participants : [],
              lastVisible: null,
              loading: false,
              loadingMore: false,
              hasMore: false,
            }));
            return;
          }

          const participantList = snapshot.docs.map(processDoc).filter(p => !p.bibTransferredOut);
          
          setState((prev) => ({
            ...prev,
            participants: lastDoc ? [...prev.participants, ...participantList] : participantList,
            lastVisible: snapshot.docs[snapshot.docs.length - 1],
            loading: false,
            loadingMore: false,
            hasMore: snapshot.size === PAGE_SIZE,
          }));
        },
        (err) => {
          if (!isMounted.current) return;
          console.error("[EntryList] Firestore Query Error:", err);
          
          const urlMatch = err.message.match(/(https:\/\/console\.firebase\.google\.com[^\s]+)/);
          const indexUrl = urlMatch ? urlMatch[1] : null;

          setState((prev) => ({
            ...prev,
            error: `Failed to load: ${err.message}`,
            errorUrl: indexUrl,
            loading: false,
            loadingMore: false,
          }));
        }
      );
      unsubscribeRef.current.participants = unsubscribe;
    },
    [debouncedSearchTerm, state.categoriesLoaded, mapCategoryToFirestore]
  );
  
  // REMOVED client-side filter effects (handleSearchChange, handleGenderSortToggle update state only)
  
  // Updated Effects (removed client-side filtering)
  // ... (useEffect for fetchCount remains mostly same but respects server query)


  // REMOVED fetchAllParticipants to save costs. 
  // Using paginated server-side queries instead.

  // REMOVED fetchAllParticipants to save costs. 
  // Using paginated server-side queries instead.

  const handleSearchChange = useCallback((e) => {
    const searchValue = e.target.value || "";
    setState((prev) => ({
        ...prev,
        searchTerm: searchValue,
        searchPageIndex: 0,
        selectedGroup: null,
    }));
  }, []);

  const handleGenderSortToggle = useCallback(() => {
    setState((prev) => ({
      ...prev,
      sortGenderDirection: prev.sortGenderDirection === "desc" ? "asc" : "desc",
      searchPageIndex: 0,
      selectedGroup: null,
    }));
  }, []);

  const handleCategoryChange = useCallback(
    (distance) => {
      if (distance === state.selectedCategory) return;
      setState((prev) => ({
        ...prev,
        selectedCategory: distance,
        participants: [],
        filteredParticipants: [],
        fullFilteredList: [],
        lastVisible: null,
        hasMore: true,
        searchTerm: "",
        totalFiltered: 0,
        selectedGroup: null,
      }));
      searchPageIndexRef.current = 0;
      if (state.collectionName && state.categoriesLoaded) {
        loadPage(
          state.collectionName,
          null,
          state.sortGenderDirection,
          distance
        );
      }
    },
    [
      loadPage,
      state.collectionName,
      state.sortGenderDirection,
      state.categoriesLoaded,
      state.selectedCategory,
    ]
  );

  const handleShowGroupMembers = useCallback((participant) => {
    setState((prev) => ({
      ...prev,
      selectedGroup: participant,
    }));
  }, []);

  const handleCloseModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedGroup: null,
    }));
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);



  useEffect(() => {
    if (!state.collectionName || !state.categoriesLoaded) return;
    
    const fetchCount = async () => {
        const participantsCollection = collection(db, state.collectionName);
        const baseConstraints = [];
        const searchConstraints = [];

        const firestoreCategory = mapCategoryToFirestore(state.selectedCategory);
        if (firestoreCategory && state.selectedCategory !== "all") {
            const numericDistance = parseInt(state.selectedCategory, 10);
            if (!isNaN(numericDistance)) {
                baseConstraints.push(where("distance", "==", numericDistance));
            } else if (Array.isArray(firestoreCategory) && firestoreCategory.length > 0) {
                baseConstraints.push(where("kategori", "in", firestoreCategory));
            } else if (typeof firestoreCategory === "string") {
                baseConstraints.push(where("kategori", "==", firestoreCategory));
            }
        }
        
        if (debouncedSearchTerm) {
            const term = debouncedSearchTerm.trim().toLowerCase();
            // We do not add searchKeywords to fetchCount to avoid requiring a composite index.
            // Search results are already limited to PAGE_SIZE anyway.
        }

        try {
            const q = query(participantsCollection, ...baseConstraints);
            const snapshot = await getCountFromServer(q);
            if (isMounted.current) {
                const count = snapshot.data().count;
                setState((prev) => ({
                  ...prev,
                  totalParticipants: debouncedSearchTerm ? prev.totalParticipants : count,
                  totalFiltered: count,
                }));
            }
        } catch (err) {
            if (!err.message.includes("index")) {
                console.error("Error fetching count:", err);
            }
        }
    };

    fetchCount();
  }, [
    state.collectionName,
    state.selectedCategory,
    debouncedSearchTerm,
    state.categoriesLoaded,
  ]);

  // Unifying all re-fetches to use loadPage
  useEffect(() => {
    if (state.categoriesLoaded && state.collectionName) {
      loadPage(
        state.collectionName,
        null, // lastDoc: null triggers a full reset
        state.sortGenderDirection,
        state.selectedCategory
      );
    }
  }, [
    state.categoriesLoaded,
    state.collectionName,
    state.selectedCategory,
    state.sortGenderDirection,
    debouncedSearchTerm, // Re-load when search term changes
    loadPage,
  ]);



  // Effect to re-run filter/slice when SEARCH TERM changes (without re-fetching from server if data is loaded)
  // REMOVED - Server side search handles this via loadPage in the useEffect below

  // Effect for Sort Change (Client Side)
  // REMOVED - Server side sort handles this via loadPage

  const loadMore = useCallback(() => {
    if (state.loading || state.loadingMore || !state.hasMore) return;
    let nextLastVisible = state.lastVisible;
    if (!nextLastVisible && state.participants.length > 0) {
      nextLastVisible =
        state.participants[state.participants.length - 1]?.docRef || null;
    }
    if (!nextLastVisible) return;
    loadPage(
      state.collectionName,
      nextLastVisible,
      state.sortGenderDirection,
      state.selectedCategory
    );
  }, [
    state.lastVisible,
    state.loading,
    state.loadingMore,
    state.collectionName,
    state.sortGenderDirection,
    state.hasMore,
    state.participants,
    state.selectedCategory,
    loadPage,
  ]);

  const rangeText = `${state.participants.length} of ${state.totalParticipants} Registered`;

  if (state.error) {
    return (
      <section className="w-full min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
        <p className="text-lg text-gray-600 mb-4">{state.error}</p>
        <p className="text-sm text-gray-500 mb-4">Please check the console log for error details.</p>
        {state.errorUrl && (
             <a 
                href={state.errorUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white bg-lime-600 px-4 py-2 rounded-lg text-sm mb-4 inline-block hover:bg-lime-700 transition"
             >
                Create Required Index
             </a>
        )}
        <Link to="/" className="text-lime-600 underline text-base">
          Back to Home
        </Link>
      </section>
    );
  }

  // ... (getStatusBadge, getQualifiedBadge, getPaymentBadge same as before)

  const getStatusBadge = (status) => {
    if (status === "qualified") {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <FontAwesomeIcon icon={faCheckCircle} /> Confirmed
          </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
             Pending
        </span>
    );
  };

  const getQualifiedBadge = (status) => {
      if (status === "qualified") {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <FontAwesomeIcon icon={faCheckCircle} /> Qualified
            </span>
          );
      }
       return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              <FontAwesomeIcon icon={faClock} /> Review
            </span>
          );
  };

  const getPaymentBadge = (status) => {
      if (status === "settlement") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <FontAwesomeIcon icon={faCheckCircle} /> Paid
            </span>
          );
      }
      if (status === "pending") {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              <FontAwesomeIcon icon={faClock} /> Pending
            </span>
          );
      }
       return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <FontAwesomeIcon icon={faTimesCircle} /> Unpaid
            </span>
          );
  };
          
          return (
    <section className="w-full min-h-screen py-20 text-center text-lime-600 flex-col justify-center relative bg-white">
      <div className="relative z-1 container mx-auto px-4">
        <motion.div
          className="mb-6 flex flex-col gap-4 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <h3 className="text-lg md:text-xl font-black uppercase text-lime-600 text-center block md:hidden">
            {state.eventTitle}
          </h3>
          <div className="w-full max-w-md mx-auto md:ml-0">
            <InputField
              name="search"
              type="text"
              value={state.searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name..."
              aria-label="Search participant by name"
              startIcon={
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              }
            />
          </div>

          <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-4">
            <h3 className="text-lg md:text-xl font-black uppercase text-lime-600 md:text-right hidden md:block">
              {state.eventTitle}
            </h3>
            {state.categories.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {state.categories.map((category) => (
                  <CategoryButton
                    key={category.distance}
                    category={category}
                    selectedCategory={state.selectedCategory}
                    onClick={handleCategoryChange}
                  />
                ))}
              </div>
            )}
          </div>
        </motion.div>
        <motion.div
          className="bg-white rounded-2xl border border-gray-200 text-left relative overflow-hidden max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          {/* Legend - compact for mobile */}
          <div className="p-3 md:p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-x-4 md:gap-x-8 gap-y-1 text-xs md:text-sm text-gray-600">
              <div className="flex gap-2 md:gap-4">
                <span><strong>R</strong>: Registered</span>
                <span><strong>Q</strong>: Qualified</span>
                <span><strong>P</strong>: Paid</span>
              </div>
            {/* ... */}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  {/* ... Headers ... */}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-12 hidden md:table-cell">
                    No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Category
                  </th>
                   <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:text-lime-600 hidden md:table-cell"
                    onClick={handleGenderSortToggle}
                    aria-label="Sort by gender"
                  >
                    <span className="flex items-center justify-center">
                      Gender
                      {state.sortGenderDirection && (
                        <FontAwesomeIcon
                          icon={
                            state.sortGenderDirection === "asc"
                              ? faSortUp
                              : faSortDown
                          }
                          className="ml-1 text-gray-500"
                        />
                      )}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Nationality
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-8 md:w-12">
                    R
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-8 md:w-12">
                    Q
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-8 md:w-12">
                    P
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.loading ? (
                  <SkeletonRow />
                ) : state.participants.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-4 text-center text-sm text-gray-600"
                    >
                      {state.searchTerm
                        ? "No participants match the search."
                        : state.selectedCategory !== "all"
                        ? "No participants registered for this category yet."
                        : "No participants registered yet."}
                    </td>
                  </tr>
                ) : (
                  state.participants.map((participant, index) => {
                    // Determine status for each step
                    const isRegistered = true; // All in list are registered
                    const isQualified = participant.administration_status === "qualified";
                    const isPaid = participant.statusPembayaran === "settlement" || participant.statusPembayaran === "capture";
                    
                    // Format gender for display
                    const genderShort = participant.gender === "Male" || participant.gender === "Laki-laki" ? "Male" : 
                           participant.gender === "Female" || participant.gender === "Perempuan" ? "Female" : 
                           participant.gender || "Unknown";

                    return (
                      <tr
                        key={participant.id}
                        className="border-b border-gray-200"
                      >
                        {/* No - hidden on mobile */}
                        <td className="px-4 py-4 text-sm text-gray-500 text-center hidden md:table-cell">
                          {index + 1}
                        </td>
                        {/* Name with gender/kota below on mobile */}
                        <td className="px-4 py-3 md:py-4">
                          <div className={`text-sm whitespace-nowrap md:whitespace-normal ${
                            participant.gender === "Perempuan" || participant.gender === "F" || participant.gender === "Female"
                              ? "text-lime-600"
                              : "text-gray-600"
                          }`}>
                            {participant.nama
                              ? participant.nama.toUpperCase()
                              : "-"}
                            {participant.category === "Mix Group (20K)" && (
                              <FontAwesomeIcon
                                icon={faUsers}
                                className="ml-2 text-lime-600 cursor-pointer"
                                title="View group members"
                                onClick={() => handleShowGroupMembers(participant)}
                              />
                            )}
                          </div>
                          {/* Mobile only: show gender and nationality below name */}
                          <div className="md:hidden text-xs text-gray-400 mt-1">
                            {genderShort} • {participant.nationality || "Indonesia"}
                          </div>
                        </td>
                        {/* Category - hidden on mobile */}
                        <td className="px-4 py-4 text-sm text-gray-600 text-center hidden md:table-cell">
                          {participant.category || "-"}
                        </td>
                        {/* Gender - hidden on mobile */}
                        <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell text-center">
                          {genderShort}
                        </td>
                        {/* Nationality - hidden on mobile */}
                        <td className="px-4 py-4 text-sm text-gray-600 text-center hidden md:table-cell">
                          {participant.nationality || "Indonesia"}
                        </td>
                        {/* R - Registered */}
                        <td className="px-1 md:px-2 py-4 text-center">
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-lime-500 text-sm md:text-base"
                          />
                        </td>
                        {/* Q - Qualified */}
                        {/* Q - Qualified */}
                        <td className="px-1 md:px-2 py-4 text-center">
                          {isQualified || isPaid ? (
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="text-lime-500 text-sm md:text-base"
                            />
                          ) : participant.administration_status === "rejected" ? (
                             <FontAwesomeIcon
                              icon={faTimesCircle}
                              className="text-amber-500 text-sm md:text-base"
                              title="Rejected"
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faClock}
                              className="text-gray-400 text-sm md:text-base"
                            />
                          )}
                        </td>
                        {/* P - Paid */}
                        <td className="px-1 md:px-2 py-4 text-center">
                          {isPaid ? (
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="text-lime-500 text-sm md:text-base"
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faClock}
                              className="text-gray-400 text-sm md:text-base"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end items-center px-4 py-2">
            <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
            <button
              onClick={loadMore}
              disabled={state.loading || state.loadingMore || !state.hasMore}
              className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1
                ${
                  state.loading || state.loadingMore || !state.hasMore
                    ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                    : "bg-lime-600 text-white hover:bg-lime-700"
                }`}
              aria-label={
                state.hasMore
                  ? "Muat lebih banyak peserta"
                  : "Akhir daftar peserta"
              }
            >
              <span>{state.hasMore ? "Load More" : "End of list"}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>
        </motion.div>
      </div>

      {state.selectedGroup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-lg py-6 px-0 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium  text-lime-600 mb-4 px-4 text-center">
              Group Members: {state.selectedGroup.nama}
            </h3>
            {state.selectedGroup.participants.length > 0 ? (
              <table className="w-full table-auto border-none">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                      Name
                    </th>
                    <th className="px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                      Gender
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {state.selectedGroup.participants.map(
                    (member, index, array) => (
                      <tr
                        key={`${member.namaDepan}-${member.namaBelakang}-${index}`}
                        className={
                          index === array.length - 1
                            ? ""
                            : "border-b border-gray-200"
                        }
                      >
                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                          {`${member.namaDepan || ""} ${
                            member.namaBelakang || ""
                          }`.trim() || "Name not available"}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                          {member.gender || "Unknown"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-600 px-4 text-center">
                No group members available. Data may be incomplete.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
});
